import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import {
  initializeFaro as coreInit,
  getWebInstrumentations,
  ReactIntegration,
  ReactRouterVersion,
} from "@grafana/faro-react";
import { faro } from "@grafana/faro-core";
import type { Faro } from "@grafana/faro-core";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";

import { faroConfig } from "../configs/faro.config";

export async function initializeFaro(): Promise<Faro> {
  const faroConfiguration = await faroConfig();

  const bots =
    "(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";
  const botsRegex = new RegExp(bots, "i");

  // set a sampling factor of 0 for bots
  const samplingFactor = botsRegex.test(navigator.userAgent) ? 0 : 1;

  const FARO_COLLECTOR_URL = faroConfiguration.faro.faro_collector_url;
  const FARO_API_KEY = faroConfiguration.faro.faro_api_key;
  const FARO_APP_NAME = faroConfiguration.faro.faro_app_name;
  const FARO_APP_VERSION = faroConfiguration.faro.faro_app_version;
  const FARO_APP_ENV = faroConfiguration.faro.faro_app_env;
  const FARO_CORS_URL =
    faroConfiguration.faro.faro_cors_url || "^.*.albertsons.com.*$";
  //Instrument only required domains
  //This will instrument only the resources for the current domain
  //const ignoreUrls = [new RegExp(`^(?!.*${window.location.hostname})`)];
  //To manually specify the urls
  const ignoreUrls = [new RegExp(`^(?!.*.albertsons.com)`), FARO_COLLECTOR_URL];
  //To instrument all urls
  //const ignoreUrls = [new RegExp(`.*`)];

  //Add all the service backends where we want to propogate OTEL headers for co-relation
  const corsUrls = new RegExp(`${FARO_CORS_URL}`);

 coreInit({
    url: FARO_COLLECTOR_URL,
    apiKey: FARO_API_KEY,
    trackWebVitalsAttribution: true,
    sessionTracking: {
      enabled: true,
      persistent: true,
      // apply the sampling factor
      samplingRate: samplingFactor,
    },
    instrumentations: [
      ...getWebInstrumentations({
        // Optional, if you want to disable the console instrumentation use false - this will send all logs and levels printed in app
        captureConsole: true,
      }),
      new TracingInstrumentation({
        instrumentations: [
          new DocumentLoadInstrumentation(),
          new FetchInstrumentation({
            ignoreUrls,
            propagateTraceHeaderCorsUrls: [corsUrls],
          }),
          new XMLHttpRequestInstrumentation({
            ignoreUrls,
            propagateTraceHeaderCorsUrls: [corsUrls],
          }),
        ],
      }),
      new ReactIntegration({
        router: {
          version: ReactRouterVersion.V6,
          dependencies: {
            createRoutesFromChildren,
            matchRoutes,
            Routes,
            useLocation,
            useNavigationType,
          },
        },
      }),
    ],
    app: {
      name: FARO_APP_NAME,
      version: FARO_APP_VERSION,
      environment: FARO_APP_ENV,
    },
    ignoreUrls: [FARO_COLLECTOR_URL],
  });
 

  // Log initialization
  faro.api.pushLog([
    "Faro was initialized for APP_NAME:" +
      FARO_APP_NAME +
      " APP_VERSION:" +
      FARO_APP_VERSION +
      " APP_ENVIRONMENT:" +
      FARO_APP_ENV,
  ]);

  return faro;
}
